import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Info } from 'lucide-react'
import { EntityPageHeader } from '@/shared/components'
import { formateursAdminApi } from '../api/formateurs-admin.api'
import { useStructuresActives } from '@/features/structures/hooks/use-structures'

const formateurSchema = z.object({
  prenom: z.string().min(1, 'Le prénom est obligatoire'),
  nom: z.string().min(1, 'Le nom est obligatoire'),
  email: z.string().email('Email invalide').min(1, "L'email est obligatoire"),
  telephone: z.string().optional(),
})

type FormateurFormValues = z.infer<typeof formateurSchema>

export function FormateurCreatePage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { data: structures } = useStructuresActives()
  const [selectedStructureIds, setSelectedStructureIds] = useState<number[]>([])

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormateurFormValues>({
    resolver: zodResolver(formateurSchema),
  })

  const mutation = useMutation({
    mutationFn: formateursAdminApi.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'formateurs'] })
      navigate('/formateurs')
    },
  })

  const toggleStructure = (id: number) => {
    setSelectedStructureIds((prev) =>
      prev.includes(id) ? prev.filter((s) => s !== id) : [...prev, id],
    )
  }

  const onSubmit = (data: FormateurFormValues) => {
    mutation.mutate({ ...data, structureIds: selectedStructureIds })
  }

  return (
    <div className="space-y-6">
      <EntityPageHeader
        title={t('admin.formateurs.createTitle', 'Nouveau formateur')}
        subtitle={t('admin.formateurs.createSubtitle', 'Créer un compte formateur')}
        backTo="/formateurs"
      />

      <div className="mx-auto max-w-lg space-y-6">
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          <div className="rounded-xl border border-neutral-200 bg-white p-6 shadow-sm space-y-5">
            <h2 className="text-base font-semibold text-neutral-900">
              {t('admin.formateurs.formTitle', 'Informations du formateur')}
            </h2>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="mb-1.5 block text-sm font-medium text-neutral-700">
                  {t('common.firstName', 'Prénom')} <span className="text-red-400">*</span>
                </label>
                <input
                  {...register('prenom')}
                  type="text"
                  placeholder="Jean"
                  className="h-10 w-full rounded-lg border border-neutral-300 bg-neutral-50 px-3 text-sm hover:border-neutral-400 focus:border-primary focus:bg-white focus:outline-none focus:ring-2 focus:ring-primary/25"
                />
                {errors.prenom && (
                  <p className="mt-1 text-xs text-red-500">{errors.prenom.message}</p>
                )}
              </div>
              <div>
                <label className="mb-1.5 block text-sm font-medium text-neutral-700">
                  {t('common.lastName', 'Nom')} <span className="text-red-400">*</span>
                </label>
                <input
                  {...register('nom')}
                  type="text"
                  placeholder="Dupont"
                  className="h-10 w-full rounded-lg border border-neutral-300 bg-neutral-50 px-3 text-sm hover:border-neutral-400 focus:border-primary focus:bg-white focus:outline-none focus:ring-2 focus:ring-primary/25"
                />
                {errors.nom && (
                  <p className="mt-1 text-xs text-red-500">{errors.nom.message}</p>
                )}
              </div>
            </div>

            <div>
              <label className="mb-1.5 block text-sm font-medium text-neutral-700">
                {t('common.email', 'Email')} <span className="text-red-400">*</span>
              </label>
              <input
                {...register('email')}
                type="email"
                placeholder="jean.dupont@mail.fr"
                className="h-10 w-full rounded-lg border border-neutral-300 bg-neutral-50 px-3 text-sm hover:border-neutral-400 focus:border-primary focus:bg-white focus:outline-none focus:ring-2 focus:ring-primary/25"
              />
              {errors.email && (
                <p className="mt-1 text-xs text-red-500">{errors.email.message}</p>
              )}
            </div>

            <div>
              <label className="mb-1.5 block text-sm font-medium text-neutral-700">
                {t('common.phone', 'Téléphone')}
              </label>
              <input
                {...register('telephone')}
                type="tel"
                placeholder="06 12 34 56 78"
                className="h-10 w-full rounded-lg border border-neutral-300 bg-neutral-50 px-3 text-sm hover:border-neutral-400 focus:border-primary focus:bg-white focus:outline-none focus:ring-2 focus:ring-primary/25"
              />
            </div>
          </div>

          {/* Structures */}
          {structures && structures.length > 0 && (
            <div className="rounded-xl border border-neutral-200 bg-white p-6 shadow-sm space-y-3">
              <h2 className="text-base font-semibold text-neutral-900">Structures</h2>
              <p className="text-xs text-neutral-500">Sélectionnez les structures rattachées à ce formateur</p>
              <div className="flex flex-wrap gap-2">
                {structures.map((s) => {
                  const isSelected = selectedStructureIds.includes(s.id)
                  return (
                    <button
                      key={s.id}
                      type="button"
                      onClick={() => toggleStructure(s.id)}
                      className={`inline-flex items-center gap-1.5 rounded-lg border px-3 py-2 text-xs font-medium transition-all ${
                        isSelected
                          ? 'border-primary/40 bg-primary/10 text-primary shadow-sm'
                          : 'border-neutral-200 bg-white text-neutral-500 hover:border-neutral-300'
                      }`}
                    >
                      <span className={`h-2 w-2 rounded-full ${isSelected ? 'bg-primary' : 'bg-neutral-300'}`} />
                      {s.nom}
                      {s.ville && <span className="text-neutral-400">({s.ville})</span>}
                    </button>
                  )
                })}
              </div>
            </div>
          )}

          {/* Info box */}
          <div className="flex items-start gap-3 rounded-xl border border-blue-100 bg-blue-50 p-4">
            <Info className="mt-0.5 h-4 w-4 shrink-0 text-blue-500" />
            <div className="text-xs text-blue-700">
              <p className="font-medium">{t('admin.formateurs.infoTitle', 'À la création :')}</p>
              <ul className="mt-1 list-disc space-y-0.5 pl-4">
                <li>{t('admin.formateurs.infoLogin', 'Un login sera généré automatiquement (prénom.nom)')}</li>
                <li>{t('admin.formateurs.infoPassword', 'Un mot de passe temporaire sera généré')}</li>
                <li>{t('admin.formateurs.infoEmail', "Un email d'invitation sera envoyé avec les identifiants")}</li>
                <li>{t('admin.formateurs.infoChange', 'Le formateur devra changer son mot de passe à la première connexion')}</li>
              </ul>
            </div>
          </div>

          {/* Error */}
          {mutation.isError && (
            <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
              {t('admin.formateurs.createError', 'Erreur lors de la création du formateur')}
            </div>
          )}

          {/* Actions */}
          <div className="flex justify-end gap-3">
            <button
              type="button"
              onClick={() => navigate('/formateurs')}
              className="rounded-lg border border-neutral-300 px-4 py-2.5 text-sm font-medium text-neutral-700 hover:bg-neutral-50"
            >
              {t('common.cancel', 'Annuler')}
            </button>
            <button
              type="submit"
              disabled={mutation.isPending}
              className="rounded-lg bg-primary px-4 py-2.5 text-sm font-medium text-white hover:bg-primary/90 disabled:opacity-50"
            >
              {mutation.isPending
                ? t('admin.formateurs.creating', 'Création...')
                : t('admin.formateurs.createButton', 'Créer le formateur')}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
